import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";
import { getToken } from "../../../services/cpln";
import { LogEntry, LogsLiveResponse, WebSocketStatus, UseLogsWebSocketOptions, UseLogsWebSocketReturn } from "../types";
import { processLabels } from "../utils/labelProcessor";

const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;
const AUTH_TIMEOUT_MS = 10000; // 10 seconds to receive first message after auth

// Type for stored event handlers
interface WebSocketHandlers {
  open: ((e: Event) => void) | null;
  close: ((e: CloseEvent) => void) | null;
  error: ((e: Event) => void) | null;
  message: ((e: MessageEvent) => void) | null;
}

/**
 * Hook to manage WebSocket connection for live log streaming
 */
export function useLogsWebSocket(options: UseLogsWebSocketOptions): UseLogsWebSocketReturn {
  const { org, logsEndpoint, onMessage, onError, enabled = true } = options;

  const [status, setStatus] = useState<WebSocketStatus>("disconnected");
  const socketRef = useRef<WebSocket | null>(null);
  const manuallyStopped = useRef(false);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const lastQueryRef = useRef<string | null>(null);
  const lastStartRef = useRef<string | undefined>(undefined);

  // Connection guard refs to prevent duplicate connections
  const isConnectingRef = useRef(false);
  const pendingConnectionRef = useRef<AbortController | null>(null);

  // Auth timeout ref - cleared when first valid message received
  const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store event handlers for proper cleanup
  const handlersRef = useRef<WebSocketHandlers>({
    open: null,
    close: null,
    error: null,
    message: null,
  });

  // Keep callbacks in refs to avoid stale closures
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Cleanup function - removes event listeners before closing socket
  const cleanup = useCallback(() => {
    // Cancel any pending connection attempt
    if (pendingConnectionRef.current) {
      pendingConnectionRef.current.abort();
      pendingConnectionRef.current = null;
    }

    // Clear reconnect timer
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear auth timeout
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }

    // Remove event listeners and close socket
    if (socketRef.current) {
      const ws = socketRef.current;

      // Remove all event listeners before closing
      if (handlersRef.current.open) {
        ws.removeEventListener("open", handlersRef.current.open);
      }
      if (handlersRef.current.close) {
        ws.removeEventListener("close", handlersRef.current.close);
      }
      if (handlersRef.current.error) {
        ws.removeEventListener("error", handlersRef.current.error);
      }
      if (handlersRef.current.message) {
        ws.removeEventListener("message", handlersRef.current.message);
      }

      // Reset handlers
      handlersRef.current = { open: null, close: null, error: null, message: null };

      // Close the socket
      ws.close();
      socketRef.current = null;
    }

    isConnectingRef.current = false;
  }, []);

  // Connect to WebSocket
  const connect = useCallback(
    async (query: string, start?: string) => {
      if (!enabled || !org || !logsEndpoint) return;

      // Prevent multiple simultaneous connection attempts
      if (isConnectingRef.current) {
        return;
      }

      // Cancel any pending connection
      if (pendingConnectionRef.current) {
        pendingConnectionRef.current.abort();
      }

      // Cleanup existing connection first
      cleanup();

      // Store for reconnection
      lastQueryRef.current = query;
      lastStartRef.current = start;
      manuallyStopped.current = false;

      // Mark as connecting and create abort controller
      isConnectingRef.current = true;
      const abortController = new AbortController();
      pendingConnectionRef.current = abortController;

      setStatus("connecting");

      try {
        // Get auth token
        const token = await getToken();

        // Check if connection was aborted during token fetch
        if (abortController.signal.aborted) {
          isConnectingRef.current = false;
          return;
        }

        // Build WebSocket URL (token sent via first message, not in URL)
        const wsParams: Record<string, string> = {
          limit: "1000",
          query,
        };

        if (start) {
          wsParams.start = start;
        }

        const querystring = new URLSearchParams(wsParams).toString();
        const baseUrl = logsEndpoint.replace("https://", "wss://");
        const url = `${baseUrl}/logs/org/${org}/loki/api/v1/tail?${querystring}`;

        // Final abort check before creating WebSocket
        if (abortController.signal.aborted) {
          isConnectingRef.current = false;
          return;
        }

        // Create WebSocket connection
        const ws = new WebSocket(url);
        socketRef.current = ws;

        // Create and store event handlers for proper removal later
        const openHandler = () => {
          // Send authentication as first message
          const authMessage = JSON.stringify({ token: token });
          ws.send(authMessage);
          setStatus("connected");
          reconnectAttempts.current = 0;
          // NOTE: Keep isConnectingRef.current = true until auth confirmed or fails

          // Start auth timeout - if no message received, auth likely failed
          authTimeoutRef.current = setTimeout(() => {
            // Only trigger if socket is still open and we haven't received any messages
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
              setStatus("error");
              manuallyStopped.current = true;
              isConnectingRef.current = false;
              onErrorRef.current?.(new Error("Authentication timeout - no response from server"));
              ws.close();
            }
          }, AUTH_TIMEOUT_MS);
        };

        const closeHandler = (event: CloseEvent) => {
          // Ignore close events from stale sockets (we've already created a new one)
          if (socketRef.current !== ws) {
            return;
          }

          // Clear auth timeout if pending
          if (authTimeoutRef.current) {
            clearTimeout(authTimeoutRef.current);
            authTimeoutRef.current = null;
          }

          // Code 1006 = Abnormal Closure (connection lost without close frame)
          // Log to Sentry for monitoring
          if (event.code === 1006) {
            const errorMessage = `WebSocket closed abnormally (code 1006): ${event.reason || "No reason provided"}`;
            Sentry.captureMessage(errorMessage, {
              level: "error",
              extra: {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                url: url,
              },
            });
          }

          // Code 1008 = Policy Violation (auth failure) - don't reconnect, report error
          if (event.code === 1008) {
            setStatus("error");
            socketRef.current = null;
            isConnectingRef.current = false;
            manuallyStopped.current = true;
            onErrorRef.current?.(new Error(event.reason || "Authentication failed"));
            return;
          }

          setStatus("disconnected");
          socketRef.current = null;
          isConnectingRef.current = false;

          // Auto-reconnect if not manually stopped
          if (!manuallyStopped.current && lastQueryRef.current) {
            if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
              reconnectAttempts.current++;
              reconnectTimeoutRef.current = setTimeout(() => {
                // Double-check we should still reconnect
                if (lastQueryRef.current && !manuallyStopped.current) {
                  connect(lastQueryRef.current, lastStartRef.current);
                }
              }, RECONNECT_DELAY);
            }
          }
        };

        const errorHandler = (_event: Event) => {
          setStatus("error");
          manuallyStopped.current = true;
          isConnectingRef.current = false;
          // Provide detailed error information for debugging
          const errorDetails = [
            "WebSocket connection failed",
            `URL: ${url.substring(0, 100)}...`,
            "Check network connectivity and authentication.",
          ].join("\n");
          onErrorRef.current?.(new Error(errorDetails));
        };

        const messageHandler = (event: MessageEvent) => {
          // Clear auth timeout on first message - auth succeeded
          if (authTimeoutRef.current) {
            clearTimeout(authTimeoutRef.current);
            authTimeoutRef.current = null;
          }

          // Auth confirmed - now safe to allow new connections
          isConnectingRef.current = false;

          try {
            const res = JSON.parse(event.data);

            // Handle authentication errors from backend
            if (res.error) {
              setStatus("error");
              manuallyStopped.current = true;
              onErrorRef.current?.(new Error(res.error));
              ws.close();
              return;
            }

            // Cast to expected type for log processing
            const logResponse = res as LogsLiveResponse;
            const entries: LogEntry[] = [];

            if (logResponse.streams) {
              for (const stream of logResponse.streams) {
                const labels = processLabels(stream.stream);

                for (const [timestampNs, log] of stream.values) {
                  const timestampMs = Math.floor(Number(timestampNs) / 1000000);

                  entries.push({
                    id: uuidv4(),
                    timestamp: timestampMs,
                    labels,
                    log,
                  });
                }
              }
            }

            if (entries.length > 0) {
              onMessageRef.current(entries);
            }
          } catch {
            // Silently ignore parse errors
          }
        };

        // Store handlers for later removal
        handlersRef.current = {
          open: openHandler,
          close: closeHandler,
          error: errorHandler,
          message: messageHandler,
        };

        // Add event listeners
        ws.addEventListener("open", openHandler);
        ws.addEventListener("close", closeHandler);
        ws.addEventListener("error", errorHandler);
        ws.addEventListener("message", messageHandler);

        // Clear the pending connection ref since we've successfully created the socket
        pendingConnectionRef.current = null;
      } catch (e) {
        isConnectingRef.current = false;
        pendingConnectionRef.current = null;

        // Only set error state if not aborted
        if (!abortController.signal.aborted) {
          setStatus("error");
          const errorMessage = e instanceof Error ? e.message : "Unknown error";
          onErrorRef.current?.(new Error(`Failed to establish live connection: ${errorMessage}`));
        }
      }
    },
    [enabled, org, logsEndpoint, cleanup],
  );

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    manuallyStopped.current = true;
    cleanup();
    setStatus("disconnected");
    lastQueryRef.current = null;
    lastStartRef.current = undefined;
    reconnectAttempts.current = 0;
  }, [cleanup]);

  // Cleanup on unmount - empty deps to only run on unmount
  useEffect(() => {
    return () => {
      manuallyStopped.current = true;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    connect,
    disconnect,
    status,
  };
}
